import { createSelector } from 'reselect';

// Selectors
import { getQueryParam } from 'store/reducers/queryParams/selectors';
import { getAppName } from 'store/reducers/userInteractions/selectors';

// Constants
import { APP_NAMES } from 'constants/appName';
import { COMMON_I18N_PREFIX } from 'constants/localizationKeys';

// Utils
import {
  useMicrositeStaticUrl,
  getMetaTagContentByName,
  isAbsoluteURL
} from 'utils/DOM';

const { SELF_CHECK_IN, STATUS_TRACKER, CONSUMER_8 } = APP_NAMES;

export const getDealerData = ({ dealerData }) => dealerData;

export const getDlrLoadConfig = createSelector(
  getDealerData,
  ({ dlrLoadConfig }) => dlrLoadConfig
);

export const getDlrBackgroundImage = createSelector(
  getDlrLoadConfig,
  dlrLoadConfig => {
    if (dlrLoadConfig) {
      return dlrLoadConfig.c8DlrBackgroundImage || '';
    }
    return '';
  }
);

export const getDealerLogo = createSelector(
  getDealerData,
  ({ dealerLogoURL }) => {
    if (dealerLogoURL) {
      const endpoint = `${getMetaTagContentByName('xtime:consumerEndpoint')}`;
      const isMicrositeProd = useMicrositeStaticUrl();
      const origin =
        isMicrositeProd && !isAbsoluteURL(dealerLogoURL) ? `//${endpoint}` : '';
      return `${origin}${dealerLogoURL}`;
    }
  }
);

export const getClickTrackingConfig = createSelector(
  getDlrLoadConfig,
  dlrLoadConfig => {
    if (dlrLoadConfig) {
      return dlrLoadConfig.clickTrackingConfig || {};
    }
    return {};
  }
);

export const getAsburyConfig = createSelector(
  getDlrLoadConfig,
  dlrLoadConfig => {
    if (dlrLoadConfig) {
      return dlrLoadConfig.asbury || {};
    }
    return {};
  }
);

export const getAsburyGAIDs = createSelector(
  getAsburyConfig,
  ({ googleAnalyticsID }) => googleAnalyticsID && googleAnalyticsID.split(';')
);

export const getUniversalAnalytics = createSelector(
  getClickTrackingConfig,
  clickTrackingConfig => {
    if (clickTrackingConfig) {
      return clickTrackingConfig.universalAnalytics || [];
    }
    return [];
  }
);

export const getDotomiDealerId = createSelector(
  getClickTrackingConfig,
  clickTrackingConfig => {
    if (clickTrackingConfig.dotomi) {
      return clickTrackingConfig.dotomi.dealerId;
    }
    return false;
  }
);

export const getCobalt = createSelector(
  getClickTrackingConfig,
  clickTrackingConfig => {
    if (clickTrackingConfig) {
      return clickTrackingConfig.cobalt;
    }
    return false;
  }
);

export const getPixall = createSelector(
  getClickTrackingConfig,
  clickTrackingConfig => {
    if (clickTrackingConfig) {
      return clickTrackingConfig.pixall;
    }
    return false;
  }
);

export const getMileOne = createSelector(
  getClickTrackingConfig,
  clickTrackingConfig => {
    if (clickTrackingConfig) {
      return clickTrackingConfig.mileOne;
    }
    return false;
  }
);

export const getShift = createSelector(
  getClickTrackingConfig,
  clickTrackingConfig => {
    if (clickTrackingConfig) {
      return clickTrackingConfig.shiftAnalytics || null;
    }
    return null;
  }
);

export const getDealerServerUrl = createSelector(
  getDealerData,
  dealerData => dealerData.dealerServerUrl
);

const getClickTrackingDetails = createSelector(getDealerData, dealerData => {
  if (dealerData) {
    return dealerData.clickTrackingDetails;
  }
  return null;
});

export const getConversantTrackingDetails = createSelector(
  getClickTrackingDetails,
  clickTrackingDetails => {
    if (clickTrackingDetails) {
      const conversantTrackingDetails = clickTrackingDetails.find(
        trackerDetails => trackerDetails.type === 'CONVERSANT'
      );
      if (conversantTrackingDetails) {
        return conversantTrackingDetails.config;
      }
    }
    return null;
  }
);

export const getDealerDataParams = localeCode =>
  createSelector(
    getQueryParam('brandvariant'),
    getQueryParam('variant'),
    getQueryParam('locale'),
    getQueryParam('country'),
    getQueryParam('language'),
    getAppName,
    (brandVariant, variant, locale, country, language, appName) => {
      let getLanguage = language;
      let getCountry = country;
      let getLocale = locale;
      if (!localeCode) {
        if (getCountry && getLanguage) {
          getLocale = getLanguage + '_' + getCountry;
        }
      } else {
        getLocale = localeCode;
      }
      if (getLocale) {
        const localeParts = getLocale.split('_');
        if (localeParts.length === 2) {
          getLanguage = localeParts[0];
          getCountry = localeParts[1];
        }
      }
      let localizationPattern;
      switch (appName) {
        case SELF_CHECK_IN:
          localizationPattern = `c8.self.check.in,${COMMON_I18N_PREFIX},c8.start.page.logo.override`;
          break;
        case STATUS_TRACKER:
          localizationPattern = `c8.status.tracker,${COMMON_I18N_PREFIX},c8.start.page.logo.override`;
          break;
        case CONSUMER_8:
        default:
          localizationPattern = 'c8.';
      }
      return {
        localizationPattern,
        variant,
        brandVariant,
        locale: getLocale,
        countryCode: getCountry,
        languageCode: getLanguage
      };
    }
  );

export const getCompany = companyName =>
  createSelector(getDlrLoadConfig, ({ companies }) => {
    return companies.filter(company => {
      return company.name === companyName;
    });
  });



// WEBPACK FOOTER //
// ./src/store/reducers/dealerData/selectors.js